/**
 * src/services/AnalyticsService.js
 *
 * Fire-and-forget analytics tracking service for Lytning Usage API.
 * Sends events to https://lna.pos.co.zm/SynergyApi/api/v1/usage/record
 * app_key 'cash.co.zm' registered — verify at GET /usage/apps if Invalid app_key errors
 */

const USAGE_API = 'https://lna.pos.co.zm/SynergyApi/api/v1/usage/record'
const APP_KEY = 'cash.co.zm' // registered app_key — verify at GET /usage/apps if Invalid app_key errors

/**
 * Generate or retrieve device ID from localStorage
 * Required by Lytning Usage API
 */
const getDeviceId = () => {
  let deviceId = localStorage.getItem('chrilan_device_id')
  if (!deviceId) {
    deviceId = 'dev_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36)
    localStorage.setItem('chrilan_device_id', deviceId)
  }
  return deviceId
}

/**
 * Extract user context from Vuex LoginModule
 */
const getUserContext = () => {
  const login = window.vuexStore?.state?.LoginModule
  if (!login) return {}
  return {
    user_id: login?.userInfo?.user_id || null,
    customer_id: login?.api_key || null,
    store_id: login?.store_id || null
  }
}

/**
 * Internal postEvent helper — fire-and-forget, no await, errors silently swallowed
 */
const postEvent = (feature, action, metadata = {}) => {
  if (navigator.onLine === false) return

  const payload = {
    app_key: APP_KEY,
    feature,
    action,
    device_id: getDeviceId(),
    ...getUserContext(),
    ...metadata
  }

  fetch(USAGE_API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  }).catch(() => {})
}

/**
 * Analytics Service
 *
 * payload schema sent to POST /usage/record:
 * {
 *   app_key:    'cash.co.zm',      // Required — registered app identifier
 *   feature:   'page:Dashboard', // Required — what was interacted with (prefix: page:, btn:, form:, auth:, error:)
 *   action:    'view',           // Required — what happened (view, click, submit, submit_failed, or error message)
 *   device_id: 'dev_xxx',       // Required — per-device, localStorage persisted
 *   user_id:    null,           // Optional — null pre-auth
 *   customer_id: null,         // Optional — null pre-auth
 *   store_id:  null,           // Optional — null pre-auth
 * }
 *
 * The API ignores extra fields. platform is NOT part of the API contract.
 */
const AnalyticsService = {
  track({ feature, action, metadata = {} }) {
    postEvent(feature, action, metadata)
  },

  trackPage(pageName, extra = {}) {
    postEvent(`page:${pageName}`, 'view', extra)
  },

  trackClick(buttonName, metadata = {}) {
    postEvent(`btn:${buttonName}`, 'click', metadata)
  },

  trackForm(formName, success, metadata = {}) {
    postEvent(`form:${formName}`, success ? 'submit' : 'submit_failed', { success, ...metadata })
  },

  trackAuth(action, metadata = {}) {
    postEvent(`auth:${action}`, 'view', metadata)
  },

  trackError(errorType, message, metadata = {}) {
    postEvent(`error:${errorType}`, message, metadata)
  }
}

export default AnalyticsService
